import type { CSSProperties, ReactNode } from "react";

import { cn } from "@workspace/ui/lib/utils";

export type ShellColumns =
  | "rail+main"
  | "left+main"
  | "left+main+right";

/** JCG UI 2.0 아이콘 레일 폭. */
export const SHELL_RAIL_WIDTH = 58;

export interface ShellFrameProps {
  columns: ShellColumns;
  children: ReactNode;
  rail: ReactNode;
  left?: ReactNode;
  right?: ReactNode;
  header?: ReactNode;
  leftWidthCss?: string;
  showLeftContent?: boolean;
  leftResizeHandle?: ReactNode;
  rightWidthCss?: string;
  rightResizeHandle?: ReactNode;
  rightAsideClassName?: string;
  mainClassName?: string;
  mainId?: string;
  leftAriaLabel?: string;
  rightAriaLabel?: string;
}

/**
 * 감자 패밀리 공통 데스크톱 셸.
 *
 * JCG의 ShellFrame이 소유하던 DOM 순서, 아이콘 레일, sticky 좌우 패널,
 * 슬림 헤더, CSS 폭 변수와 모바일 first-paint 계약을 그대로 공유한다.
 * 앱별 내비게이션과 패널 내용은 슬롯으로 주입한다.
 */
export function ShellFrame({
  columns,
  children,
  rail,
  left,
  right,
  header,
  leftWidthCss,
  showLeftContent = true,
  leftResizeHandle,
  rightWidthCss,
  rightResizeHandle,
  rightAsideClassName,
  mainClassName,
  mainId,
  leftAriaLabel,
  rightAriaLabel,
}: ShellFrameProps) {
  const hasLeftColumn =
    columns === "left+main" || columns === "left+main+right";
  const hasRightColumn = columns === "left+main+right";
  const railOnly = columns === "rail+main";

  const leftCssWidth = railOnly
    ? `${SHELL_RAIL_WIDTH}px`
    : (leftWidthCss ?? `${SHELL_RAIL_WIDTH}px`);
  const rightCssWidth = rightWidthCss ?? "0px";

  const gridStyle = {
    gridTemplateColumns: hasRightColumn
      ? "var(--left-w) minmax(0, 1fr) var(--right-w)"
      : "var(--left-w) minmax(0, 1fr)",
    "--left-w": leftCssWidth,
    ...(hasRightColumn ? { "--right-w": rightCssWidth } : {}),
  } as CSSProperties;

  return (
    <div
      data-desktop-shell-frame
      className="desktop-shell-frame grid min-h-dvh"
      style={gridStyle}
    >
      {/*
        DOM 순서는 좌 → 우 → 가운데를 유지한다. JCG 광고 슬롯의 positional
        queue와 가운데 컬럼의 지연 폭 확정 계약을 깨지 않기 위한 순서다.
      */}
      <aside
        data-desktop-shell-aside
        aria-label={leftAriaLabel}
        style={{ gridColumn: "1", gridRow: "1" }}
        className="desktop-shell-aside sticky top-0 z-20 flex h-dvh self-start overflow-hidden border-r border-border bg-background"
      >
        <div aria-hidden className="h-dvh w-0 shrink-0" />
        <div className="absolute inset-0 flex">
          {rail}
          {!railOnly && showLeftContent && (
            <>
              {left}
              {hasLeftColumn && leftResizeHandle}
            </>
          )}
        </div>
      </aside>

      {hasRightColumn && (
        <aside
          data-desktop-shell-aside
          aria-label={rightAriaLabel}
          style={{ gridColumn: "3", gridRow: "1" }}
          className={cn(
            "desktop-shell-aside sticky top-0 z-20 flex h-dvh self-start overflow-hidden border-l border-border",
            rightAsideClassName ?? "bg-background",
          )}
        >
          <div aria-hidden className="h-dvh w-0 shrink-0" />
          <div className="absolute inset-0 flex flex-col">
            {rightResizeHandle}
            {right}
          </div>
        </aside>
      )}

      <main
        id={mainId}
        data-desktop-shell-main
        style={{ gridColumn: "2", gridRow: "1" }}
        className={cn("desktop-shell-main min-w-0", mainClassName)}
      >
        {header && (
          <div data-desktop-shell-header className="desktop-shell-header contents">
            {header}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

export default ShellFrame;
